import { FormEvent, useEffect, useState } from "react";
import { Modal } from "../ui/Modal";
import { Field } from "../ui/Field";
import { TextInput } from "../ui/TextInput";
import { Button } from "../ui/Button";
import { Banner } from "../ui/Banner";
import { useCreateWorkspace } from "./useCreateWorkspace";
import { slugErrorKind } from "./slugError";
import { useTranslation, apiErrorMessage } from "../i18n";
import type { Workspace } from "../api/types";

// Derive a URL-safe slug from a display name.
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);
}

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCreated: (workspace: Workspace) => void;
}

export function CreateWorkspaceModal({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation("workspace");
  const { t: tErr } = useTranslation("errors");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const create = useCreateWorkspace();

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setName("");
      setSlug("");
      setSlugEdited(false);
      create.reset();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-suggest slug from name until the user manually edits it
  useEffect(() => {
    if (!slugEdited) {
      setSlug(slugify(name));
    }
  }, [name, slugEdited]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    // Send the slug ONLY when the user actually chose one. The mirrored value
    // is a preview: the server derives from the name and steps past anything
    // reserved or taken (`Docs` → `docs-2`), but an explicit slug is treated
    // as a decision and hard-rejected instead. Forwarding the preview took
    // that second branch for everyone and made the first unreachable.
    const chosenSlug = slugEdited ? slug.trim() : "";
    create.mutate(
      { name: name.trim(), ...(chosenSlug ? { slug: chosenSlug } : {}) },
      {
        onSuccess: (ws) => onCreated(ws),
      },
    );
  }

  const slugErrKind = slugErrorKind(create.error);
  const slugError =
    slugErrKind === "reserved"
      ? t("create.slugReserved")
      : slugErrKind === "taken"
        ? t("create.slugTaken")
        : undefined;
  // Derived, not a second restatement of the code list: a banner AND a field
  // error for the same failure means one of the two lists was missed.
  const genericError =
    create.error && !slugError ? apiErrorMessage(create.error, tErr) : undefined;

  return (
    <Modal open={open} onClose={onClose} title={t("create.title")}>
      {genericError ? (
        <Banner kind="error" className="mb-4">{genericError}</Banner>
      ) : null}

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Field label={t("create.nameLabel")}>
          {({ id, ...ariaProps }) => (
            <TextInput
              id={id}
              type="text"
              autoComplete="off"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              {...ariaProps}
            />
          )}
        </Field>

        <Field label={t("create.slugLabel")} error={slugError}>
          {({ id, ...ariaProps }) => (
            <>
              <TextInput
                id={id}
                type="text"
                autoComplete="off"
                value={slug}
                onChange={(e) => {
                  setSlugEdited(true);
                  setSlug(e.target.value);
                }}
                hasError={Boolean(slugError)}
                {...ariaProps}
              />
              {name.trim() && (
                <p className="mt-1 text-xs text-slate-500">
                  {slug ? (
                    <>
                      {window.location.host}/<span className="text-slate-300">{slug}</span>
                      {!slugEdited && <span className="ml-1">{t("create.slugPreview")}</span>}
                    </>
                  ) : (
                    // The name has nothing a slug can be built from — the
                    // server would fall back to the literal "workspace".
                    // Better to say so than to land them there silently.
                    t("create.slugUndrivable")
                  )}
                </p>
              )}
            </>
          )}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            {t("create.cancel")}
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={create.isPending || !name.trim()}
          >
            {create.isPending ? t("create.creating") : t("create.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
